import React from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import css from "classnames";
import { first } from "lodash";
import { MenuItem, Grid, TextField } from "@mui/material";
import moment from "moment";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { UserRole } from "types/gql-types";
import { Viewer } from "components/auth/types";
import { ContainerCardLayout } from "components/haven-design";
import { LoadingSpinner } from "components/graphql-views";
import { sortAndFilterLists } from "pages/dashboard/settings/list-utils";
import { QUERY_INTEREST_LISTS, WAITLIST_UPDATE_METADATA } from "../query";
import { isUpdateActive } from "../utils";
import Content from "./content";
import Empty from "./content-empty";
import styles from "./index.module.css";
import RemainingTime from "./remaining-time";

type Props = {
  viewer: Viewer;
}

type ListQueryData = {
  organization: {
    interestLists: {
      id: string;
      name: string;
      type: "property" | "interest";
      scheduledUpdate: {
        enabled: boolean;
      };
    }[];
  };
}

type ListQueryVars = {
  id: string;
}

const interestLists = (viewer:Viewer, data: ListQueryData): Array<{ id: string; name: string }> => {
  const lists = (data?.organization?.interestLists || [])
    .filter((interestList) => interestList.type === "property" || interestList.type === "interest")
    .filter((interestList) => interestList.scheduledUpdate.enabled);

  return sortAndFilterLists(viewer, lists);
};

const selectedWaitlist = (
  queryData: ListQueryData | undefined,
  viewer:Viewer,
  interestListId?: string,
) => {
  if (interestListId) return interestListId;

  if (queryData) {
    const candidate = first(interestLists(viewer, queryData));

    if (candidate) {
      return candidate.id;
    }
  }

  return null;
};

const LoadingState = () => (
  <div className={css("container", styles.page)}>
    <div className={styles.loading}>
      Loading...
      <br />
      This page may take several seconds to load.
    </div>
  </div>);

const ErroredState = () => (
  <div className={css("container", styles.page)}>
    <div className={styles.loading}>
      There was a problem trying to load this page. Please try to refresh your browser or
      contact <a href="mailto:support@havenconnect.com">support</a> if the problem
      persists.
    </div>
  </div>);

const NotFoundState = () => (<h4 className="text-center">
Can not find list for your user account. Please ask your supervisor to add a list to your
account.
</h4>);

const getQueryResult = (
  selected: string | null,
) => useQuery(
  WAITLIST_UPDATE_METADATA,
  {
    variables: {
      id: selected,
    },
  },
);

const Deadline: React.FC<{ends: string}> = ({ ends }) => {
  const deadline = moment.utc(ends);
  const localDeadline = moment(deadline).local();
  const zone = moment.tz(moment.tz.guess()).zoneAbbr();

  return (<Grid size={{ sm: 12 }}>
    <div className={styles.heading}>Deadline {localDeadline.format("MMMM D, YYYY h:mm A")} {zone ?? ""}</div>
    <div className={styles.subheading}> <RemainingTime time={deadline} /> left </div>
    <br />
  </Grid>);
};

const Lists: React.FC<{
  selected: string,
  data: ListQueryData,
  viewer:Viewer
}> = ({ selected, data, viewer }) => {
  const navigate = useNavigate();

  const requestResult = getQueryResult(selected);

  if (requestResult.loading) return <LoadingState/ >;
  if (!requestResult.data || requestResult.error) return <ErroredState />;
  const {
    interestList,
    interestList: { name, id, scheduledUpdate: { ends } },
  } = requestResult.data;

  const lists = interestLists(viewer, data);

  return (
    <ContainerCardLayout title={`${name} List Update`}>
      <Grid size={{ sm: 12 }} className={styles.dropdown}>
        <div className={styles.header}>
          {lists.length > 0 && (<TextField
            select
            variant="outlined"
            label="Waitlist"
            value={id}
            margin="none"
            onChange={(event) => navigate(`/dash/manager/update/${event.target.value}`)}>
            {lists.map(({ id: listId, name: listName }) => (
              <MenuItem key={`select-${listId}`} value={listId}>{listName}</MenuItem>))}
          </TextField>
          )}
        </div>
      </Grid>
      <Deadline ends={ends} />
      <Grid size={{ sm: 12 }}>
        {
          isUpdateActive(interestList.scheduledUpdate) ? (
            <Content interestList={interestList} />
          ) : (
            <Empty interestList={interestList} />
          )
        }
      </Grid>
    </ContainerCardLayout>
  );
};

const WaitlistUpdate: React.FC<Props> = ({ viewer }) => {
  const navigate = useNavigate();
  const { listId } = useParams();
  if ((viewer.lists && viewer.lists.length <= 0) || !viewer.organizationId) {
    return <NotFoundState />;
  }
  const { data, loading, error } = useQuery<ListQueryData, ListQueryVars>(QUERY_INTEREST_LISTS, {
    variables: { id: viewer.organizationId },
  });
  if (loading) return <LoadingSpinner />;
  if (!data || error) return null;

  const selected = selectedWaitlist(data, viewer, listId);

  if (!listId && selected) {
    navigate(`/dash/manager/update/${selected}/`);
    return null;
  }
  if (!selected) return <h4 className="text-center">No properties have opted into list updates.</h4>;

  return <Lists viewer={viewer} selected={selected} data={data} />;
};

export default authorizeForRole<Record<string, unknown>>(
  [UserRole.PROPERTY_MANAGER, UserRole.SUPERVISOR],
  WaitlistUpdate as unknown as React.FC<Record<string, unknown>>,
);
